import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Button, Tooltip, Skeleton,
  Divider, Paper, TextField, InputAdornment, MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import GroupsIcon from '@mui/icons-material/Groups';
import { useSnackbar } from 'notistack';
import {
  fetchRecords,
  executePayrollRun, approvePayrollRun, generatePayslipsForRun,
  createPaymentsForRun, generateBankFileForRun, reversePayrollRun,
  fetchOvertimeData
} from '../services/api';
import ProcessPayRunPage from './ProcessPayRunPage';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const STATUS_CONFIG = {
  draft:       { color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB', label: 'Draft',      stage: 0 },
  pending:     { color: '#92400E', bg: '#FEF3C7', border: '#FDE68A', label: 'Pending',    stage: 0 },
  processing:  { color: '#1D4ED8', bg: '#DBEAFE', border: '#BFDBFE', label: 'Processing', stage: 1 },
  completed:   { color: '#0E7490', bg: '#CFFAFE', border: '#A5F3FC', label: 'Completed',  stage: 1 },
  approved:    { color: '#15803D', bg: '#DCFCE7', border: '#BBF7D0', label: 'Approved',   stage: 2 },
  reversed:    { color: '#991B1B', bg: '#FEE2E2', border: '#FECACA', label: 'Reversed',   stage: 0 },
  error:       { color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', label: 'Error',      stage: 0 },
};

const statusOf = (s) => STATUS_CONFIG[(s || 'draft').toLowerCase()] || STATUS_CONFIG.draft;

const fmtINR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};
const fmtCompact = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  if (Math.abs(num) >= 10000000) return (num / 10000000).toFixed(2) + ' Cr';
  if (Math.abs(num) >= 100000)   return (num / 100000).toFixed(2) + ' L';
  if (Math.abs(num) >= 1000)     return (num / 1000).toFixed(1) + ' K';
  return String(num);
};

function StatusBadge({ status, size = 'md' }) {
  const cfg = statusOf(status);
  const dim = size === 'sm'
    ? { h: 22, fs: 11, px: 1.2 }
    : { h: 26, fs: 12, px: 1.6 };
  return (
    <Chip
      label={cfg.label.toUpperCase()}
      size="small"
      sx={{
        height: dim.h, fontSize: dim.fs, fontWeight: 700, letterSpacing: 0.5,
        px: dim.px, borderRadius: '8px',
        bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      }}
    />
  );
}

function StatBlock({ label, value, suffix, color }) {
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography fontSize={10.5} color="#9CA3AF" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="baseline" gap={0.5}>
        <Typography fontSize={17} fontWeight={700}
          sx={{ color: color || '#111827', fontFamily: 'monospace' }}>
          {value}
        </Typography>
        {suffix && (
          <Typography fontSize={11} color="#9CA3AF">{suffix}</Typography>
        )}
      </Stack>
    </Box>
  );
}

// ── List card ───────────────────────────────────────────────────────────────
function RunCard({ run, onView, onEdit, onDelete, onQuickRun }) {
  const status   = (run.run_status || 'draft').toLowerCase();
  const isFinal  = status === 'approved' || status === 'reversed';
  const isLocked = status === 'processing' || isFinal;

  return (
    <Paper sx={{
      mb: 1.5, p: 2.2, borderRadius: '12px',
      border: '1.5px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.15s',
      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderColor: '#22c55e' },
    }}>
      {/* Header row */}
      <Stack direction="row" alignItems="flex-start" gap={2} sx={{ mb: 1.8 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          background: 'linear-gradient(135deg,#16a34a,#22c55e)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 3px 10px rgba(34,197,94,0.3)',
          flexShrink: 0,
        }}>
          <PlayArrowIcon sx={{ fontSize: 22, color: 'white' }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.3 }}>
            <Typography fontSize={16} fontWeight={700} color="#111827" noWrap>
              {run.run_name || run.run_id}
            </Typography>
            <Typography fontSize={11} fontWeight={500} color="#64748B"
              sx={{ fontFamily: 'monospace', bgcolor: '#F3F4F6', px: 0.8, py: 0.1, borderRadius: '5px' }}>
              {run.run_id}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Chip label={run.run_type || 'Regular'} size="small"
              sx={{ height: 20, fontSize: 10.5, fontWeight: 600,
                bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }} />
            <Typography fontSize={12} color="#6B7280">·</Typography>
            <Typography fontSize={12} color="#6B7280">Period {run.period_id}</Typography>
            {run.run_date && <>
              <Typography fontSize={12} color="#6B7280">·</Typography>
              <Typography fontSize={12} color="#6B7280">Run {run.run_date}</Typography>
            </>}
            {run.payroll_id && <>
              <Typography fontSize={12} color="#6B7280">·</Typography>
              <Typography fontSize={12} color="#6B7280">{run.payroll_id}</Typography>
            </>}
          </Stack>
        </Box>

        <StatusBadge status={run.run_status} />
      </Stack>

      <Divider sx={{ mb: 1.8 }} />

      {/* Stats */}
      <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 1.8 }}>
        <StatBlock label="Employees" value={fmtINR(run.total_employees || 0)} />
        <StatBlock label="Gross"       value={`₹${fmtCompact(run.total_gross || 0)}`} />
        <StatBlock label="Deductions"  value={`₹${fmtCompact(run.total_deductions || 0)}`} />
        <StatBlock label="Employer"    value={`₹${fmtCompact(run.total_employer_contrib || 0)}`} />
        <StatBlock label="Net Pay"     value={`₹${fmtCompact(run.total_net || 0)}`} color="#16a34a" />
        {run.error_count > 0 && (
          <StatBlock label="Errors" value={run.error_count} color="#DC2626" />
        )}
      </Stack>

      <Divider sx={{ mb: 1.5 }} />

      {/* Actions */}
      <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
        {!isLocked && (status === 'draft' || status === 'pending' || status === 'error' || status === 'reversed') && (
          <Button
            variant="contained" size="small"
            startIcon={<PlayArrowIcon sx={{ fontSize: 15 }} />}
            onClick={() => onQuickRun?.(run)}
            sx={{
              background: 'linear-gradient(135deg,#16a34a,#22c55e)',
              fontSize: 12.5, py: 0.6, px: 1.6,
              '&:hover': { background: 'linear-gradient(135deg,#15803d,#16a34a)' },
            }}
          >
            Run Payroll
          </Button>
        )}

        <Button
          variant="outlined" size="small"
          startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
          onClick={() => onView(run)}
          sx={{ fontSize: 12.5, py: 0.5,
            borderColor: '#E5E7EB', color: '#374151',
            '&:hover': { borderColor: '#22c55e', color: '#16a34a', bgcolor: '#f0fdf4' } }}
        >
          View Details
        </Button>

        {!isLocked && (
          <Tooltip title="Edit" arrow>
            <IconButton size="small" onClick={() => onEdit(run)}
              sx={{ width: 30, height: 30, borderRadius: '7px',
                color: '#22c55e', border: '1.5px solid #E5E7EB',
                '&:hover': { bgcolor: '#f0fdf4', borderColor: '#22c55e' } }}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {!isFinal && (
          <Tooltip title="Delete" arrow>
            <IconButton size="small" onClick={() => onDelete(run)}
              sx={{ width: 30, height: 30, borderRadius: '7px',
                color: '#EF4444', border: '1.5px solid #E5E7EB',
                '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' } }}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

// ── Stage progress strip ────────────────────────────────────────────────────
function StageStrip({ run, payslipCount, paymentCount, bankFileCount }) {
  const status = (run.run_status || 'draft').toLowerCase();
  const isCalc = ['completed','approved','reversed'].includes(status);
  const isApp  = ['approved','reversed'].includes(status);
  const isPay  = payslipCount > 0;
  const isBank = bankFileCount > 0 || paymentCount > 0;

  const stages = [
    { label: 'Calculated',  done: isCalc, icon: <PlayArrowIcon sx={{ fontSize: 14 }} /> },
    { label: 'Approved',    done: isApp,  icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    { label: 'Payslips',    done: isPay,  icon: <DescriptionIcon sx={{ fontSize: 14 }} /> },
    { label: 'Bank File',   done: isBank, icon: <AccountBalanceIcon sx={{ fontSize: 14 }} /> },
  ];

  return (
    <Stack direction="row" alignItems="center" gap={0.8} sx={{ py: 1.2 }}>
      {stages.map((s, i) => (
        <React.Fragment key={s.label}>
          <Stack direction="row" alignItems="center" gap={0.8}
            sx={{
              px: 1.2, py: 0.6, borderRadius: '8px',
              bgcolor: s.done ? '#DCFCE7' : '#F3F4F6',
              color:   s.done ? '#15803D' : '#9CA3AF',
              border:  s.done ? '1px solid #BBF7D0' : '1px solid #E5E7EB',
            }}>
            {s.icon}
            <Typography fontSize={11.5} fontWeight={700} sx={{ letterSpacing: 0.3 }}>
              {s.label.toUpperCase()}
            </Typography>
          </Stack>
          {i < stages.length - 1 && (
            <Box sx={{ width: 14, height: 2, bgcolor: stages[i+1].done ? '#22c55e' : '#E5E7EB' }} />
          )}
        </React.Fragment>
      ))}
    </Stack>
  );
}

// ── Detail page ─────────────────────────────────────────────────────────────
function RunDetail({ run, onBack, onRefresh }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading]         = useState(true);
  const [busy, setBusy]               = useState(null); // current action label
  const [currentRun, setCurrentRun]   = useState(run);
  const [results, setResults]         = useState([]);
  const [payslips, setPayslips]       = useState([]);
  const [payments, setPayments]       = useState([]);
  const [bankFiles, setBankFiles]     = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [persons, setPersons]         = useState([]);
  const [elements, setElements]       = useState([]);
  const [search, setSearch]           = useState('');
  const [lopOverrides, setLopOverrides] = useState({});
  const [otOverrides, setOtOverrides]   = useState({});
  const [workingDays, setWorkingDays]   = useState(26);

  useEffect(() => { setCurrentRun(run); }, [run]);

  const loadAll = React.useCallback(() => {
    setLoading(true);
    return Promise.all([
      fetchRecords('PAYROLL_RUN').catch(() => ({ data: { data: [] } })),
      fetchRecords('RUN_RESULT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYSLIP').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('BANK_PAYMENT_FILE').catch(() => ({ data: { data: [] } })),
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('ELEMENT_TYPE').catch(() => ({ data: { data: [] } })),
    ]).then(([pr, rr, ps, pm, bf, asn, per, el]) => {
      const fresh = (pr.data.data || []).find(r => r.run_id === run.run_id);
      if (fresh) setCurrentRun(fresh);
      setResults    ((rr.data.data  || []).filter(r => r.run_id === run.run_id));
      setPayslips   ((ps.data.data  || []).filter(p => p.run_id === run.run_id));
      setPayments   ((pm.data.data  || []).filter(p => p.run_id === run.run_id));
      setBankFiles  ((bf.data.data  || []).filter(b => b.run_id === run.run_id));
      setAssignments(asn.data.data  || []);
      setPersons    (per.data.data  || []);
      setElements   (el.data.data   || []);
      setLoading(false);
      
      const status = (fresh?.run_status || run?.run_status || 'draft').toLowerCase();
      if (['draft', 'pending', 'error', 'reversed'].includes(status)) {
        fetchOvertimeData(run.run_id).then(res => {
          if (res.data?.success && res.data.data) {
            setOtOverrides(res.data.data);
          }
        }).catch(() => {});
      }
    });
  }, [run.run_id, run?.run_status]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runAction = async (label, apiCall) => {
    setBusy(label);
    try {
      const res = await apiCall();
      enqueueSnackbar(res.data?.message || `${label} completed`, { variant: 'success' });
      await loadAll();
      onRefresh?.();
    } catch (e) {
      const msg = e.response?.data?.message || e.message;
      enqueueSnackbar(`${label} failed: ${msg}`, { variant: 'error' });
    } finally {
      setBusy(null);
    }
  };

  // Employees in scope for this payroll run (active, matching this payroll)
  const inScope = useMemo(() => {
    return assignments
      .filter(a => a.payroll_id === currentRun.payroll_id && a.active_flag === 'Y')
      .map(a => {
        const person   = persons.find(p => p.person_id === a.person_id);
        const fullName = person
          ? [person.first_name, person.last_name].filter(Boolean).join(' ')
          : null;
        return {
          assignment_id: a.assignment_id,
          employee_code: a.employee_code,
          fullName:      fullName || a.employee_code || a.assignment_id,
        };
      })
      .sort((a, b) => a.assignment_id.localeCompare(b.assignment_id, undefined, { numeric: true }));
  }, [assignments, persons, currentRun.payroll_id]);

  const totalLopDays = Object.values(lopOverrides).reduce((s, v) => s + (Number(v) || 0), 0);

  const handleRunPayroll = () => {
    const body = { working_days: workingDays, lop_overrides: {}, variable_inputs: {} };
    for (const [k, v] of Object.entries(lopOverrides)) {
      const n = Number(v) || 0;
      if (n > 0) body.lop_overrides[k] = n;
    }
    for (const [k, v] of Object.entries(otOverrides)) {
      const h = Number(v.ot_hours) || 0;
      const r = Number(v.ot_rate) || 0;
      if (h > 0) {
        body.variable_inputs[k] = { ot_hours: h, ot_rate: r };
      }
    }
    return runAction('Run Payroll', () => executePayrollRun(currentRun.run_id, body));
  };

  const assignmentMap = useMemo(() =>
    Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const personMap = useMemo(() =>
    Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);
  const elementMap = useMemo(() =>
    Object.fromEntries(elements.map(e => [e.element_id, e])), [elements]);
  const payslipMap = useMemo(() =>
    Object.fromEntries(payslips.map(p => [p.assignment_id, p])), [payslips]);
  const paymentMap = useMemo(() =>
    Object.fromEntries(payments.map(p => [p.assignment_id, p])), [payments]);

  // Aggregate RUN_RESULT by assignment_id; classification (earning/deduction) decides sign
  const employees = useMemo(() => {
    const byAsn = {};
    for (const r of results) {
      const k = r.assignment_id;
      if (!byAsn[k]) {
        byAsn[k] = { assignment_id: k, gross: 0, deductions: 0, employerContrib: 0, lines: [] };
      }
      const amt = Number(r.amount) || 0;
      const cls = (r.classification || '').toLowerCase();
      if (cls.includes('deduction') || cls.includes('tax')) {
        byAsn[k].deductions += Math.abs(amt);
      } else if (cls.includes('employer')) {
        byAsn[k].employerContrib += amt;
      } else {
        byAsn[k].gross += amt;
      }
      byAsn[k].lines.push(r);
    }
    return Object.values(byAsn).map(e => {
      const asn      = assignmentMap[e.assignment_id];
      const person   = asn ? personMap[asn.person_id] : null;
      const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;
      const payslip  = payslipMap[e.assignment_id];
      const payment  = paymentMap[e.assignment_id];
      return {
        ...e,
        assignment: asn,
        person,
        fullName: fullName || asn?.employee_code || e.assignment_id,
        net: e.gross - e.deductions,
        payslip,
        payment,
      };
    }).sort((a, b) => a.assignment_id.localeCompare(b.assignment_id, undefined, { numeric: true }));
  }, [results, assignmentMap, personMap, payslipMap, paymentMap]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(e =>
      e.assignment_id.toLowerCase().includes(q) ||
      (e.assignment?.employee_code || '').toLowerCase().includes(q) ||
      (e.fullName || '').toLowerCase().includes(q)
    );
  }, [employees, search]);

  const status     = (currentRun.run_status || 'draft').toLowerCase();
  const isCalc     = ['completed','approved','reversed'].includes(status);
  const isApp      = ['approved'].includes(status);
  const isReversed = status === 'reversed';
  const isBusy     = (label) => busy === label;
  const anyBusy    = !!busy;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
        <IconButton onClick={onBack}
          sx={{ width: 36, height: 36, borderRadius: '10px',
            border: '1.5px solid #E5E7EB', color: '#374151',
            '&:hover': { borderColor: '#22c55e', color: '#16a34a', bgcolor: '#f0fdf4' } }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={18} fontWeight={700} color="#111827">{currentRun.run_name}</Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography fontSize={12} color="#6B7280" sx={{ fontFamily: 'monospace' }}>{currentRun.run_id}</Typography>
            <Typography fontSize={12} color="#6B7280">·</Typography>
            <Typography fontSize={12} color="#6B7280">Period {currentRun.period_id}</Typography>
            <Typography fontSize={12} color="#6B7280">·</Typography>
            <Typography fontSize={12} color="#6B7280">{currentRun.run_type}</Typography>
          </Stack>
        </Box>
        <StatusBadge status={currentRun.run_status} />
      </Stack>

      {/* Summary card */}
      <Paper sx={{ p: 2.2, mb: 2, borderRadius: '12px', border: '1.5px solid #E5E7EB' }}>
        <Stack direction="row" gap={4} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <StatBlock label="Employees"     value={fmtINR(currentRun.total_employees || 0)} />
          <StatBlock label="Gross"         value={`₹${fmtINR(currentRun.total_gross || 0)}`} />
          <StatBlock label="Deductions"    value={`₹${fmtINR(currentRun.total_deductions || 0)}`} />
          <StatBlock label="Employer Cont" value={`₹${fmtINR(currentRun.total_employer_contrib || 0)}`} />
          <StatBlock label="Net Pay"       value={`₹${fmtINR(currentRun.total_net || 0)}`} color="#16a34a" />
          <StatBlock label="Total Cost"
            value={`₹${fmtINR((Number(currentRun.total_gross) || 0) + (Number(currentRun.total_employer_contrib) || 0))}`} />
        </Stack>
        <Divider />
        <StageStrip run={currentRun} payslipCount={payslips.length}
          paymentCount={payments.length} bankFileCount={bankFiles.length} />
      </Paper>

      {/* LOP Adjustment — only when run is not yet calculated */}
      {(!isCalc || isReversed) && inScope.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1.5px solid #FDE68A',
          bgcolor: '#FFFBEB' }}>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px',
              bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center',
              justifyContent: 'center' }}>
              <Typography fontSize={16}>📅</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontSize={13.5} fontWeight={700} color="#92400E">
                LOP Days Adjustment
              </Typography>
              <Typography fontSize={11.5} color="#78350F">
                Set Loss-of-Pay days per employee. Earnings will be pro-rated; deductions stay full.
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography fontSize={11.5} color="#78350F">Working days:</Typography>
              <TextField
                size="small" type="number" value={workingDays}
                onChange={e => setWorkingDays(Math.max(1, Math.min(31, Number(e.target.value) || 0)))}
                sx={{ width: 70, '& .MuiOutlinedInput-root': { bgcolor: 'white', fontSize: 12.5, borderRadius: '6px' },
                  '& input': { textAlign: 'center', py: 0.6 } }}
                InputProps={{ inputProps: { min: 1, max: 31 } }}
              />
            </Stack>
          </Stack>

          <Box sx={{ bgcolor: 'white', borderRadius: '8px', border: '1px solid #FDE68A', overflow: 'hidden' }}>
            <Stack direction="row" gap={1} sx={{ px: 1.5, py: 0.8, bgcolor: '#FFFBEB',
              borderBottom: '1px solid #FDE68A' }}>
              <Box sx={{ flex: 1 }}><Typography fontSize={10} fontWeight={700} color="#92400E" sx={{ letterSpacing: 0.5 }}>EMPLOYEE</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'center' }}><Typography fontSize={10} fontWeight={700} color="#92400E" sx={{ letterSpacing: 0.5 }}>LOP DAYS</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'right' }}><Typography fontSize={10} fontWeight={700} color="#92400E" sx={{ letterSpacing: 0.5 }}>PRO-RATA</Typography></Box>
            </Stack>
            {inScope.map((emp, i) => {
              const lop  = Number(lopOverrides[emp.assignment_id]) || 0;
              const rate = workingDays > 0 ? ((workingDays - lop) / workingDays * 100) : 0;
              return (
                <Stack key={emp.assignment_id} direction="row" gap={1} alignItems="center"
                  sx={{ px: 1.5, py: 0.8,
                    borderBottom: i === inScope.length - 1 ? 'none' : '1px solid #FEF3C7' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={12.5} fontWeight={600} color="#111827" noWrap>{emp.fullName}</Typography>
                    <Typography fontSize={10} color="#9CA3AF" sx={{ fontFamily: 'monospace' }}>
                      {emp.assignment_id}{emp.employee_code ? ` · ${emp.employee_code}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'center' }}>
                    <TextField
                      size="small" type="number" value={lop || ''}
                      onChange={e => {
                        const v = Math.max(0, Math.min(workingDays, Number(e.target.value) || 0));
                        setLopOverrides(prev => ({ ...prev, [emp.assignment_id]: v }));
                      }}
                      placeholder="0"
                      sx={{ width: 70, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '6px' },
                        '& input': { textAlign: 'center', py: 0.5 } }}
                      InputProps={{ inputProps: { min: 0, max: workingDays } }}
                    />
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'right' }}>
                    <Typography fontSize={12} fontWeight={700}
                      color={rate === 100 ? '#16a34a' : rate >= 80 ? '#92400E' : '#DC2626'}
                      sx={{ fontFamily: 'monospace' }}>
                      {rate.toFixed(1)}%
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Box>

          {totalLopDays > 0 && (
            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ mt: 1.2, px: 1.5, py: 1, bgcolor: '#FEF3C7', borderRadius: '8px',
                border: '1px solid #FDE68A' }}>
              <Typography fontSize={11.5} fontWeight={600} color="#78350F">
                Total LOP days across all employees:
              </Typography>
              <Typography fontSize={13} fontWeight={800} color="#92400E"
                sx={{ fontFamily: 'monospace' }}>
                {totalLopDays}
              </Typography>
            </Stack>
          )}
        </Paper>
      )}

      {/* Overtime Adjustments — only when run is not yet calculated */}
      {(!isCalc || isReversed) && inScope.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: '12px', border: '1.5px solid #DBEAFE', bgcolor: '#EFF6FF' }}>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#BFDBFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography fontSize={16}>⏱️</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography fontSize={13.5} fontWeight={700} color="#1E3A8A">
                Overtime Adjustments
              </Typography>
              <Typography fontSize={11.5} color="#1E40AF">
                Review and adjust calculated overtime hours and rates.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ bgcolor: 'white', borderRadius: '8px', border: '1px solid #BFDBFE', overflow: 'hidden' }}>
            <Stack direction="row" gap={1} sx={{ px: 1.5, py: 0.8, bgcolor: '#EFF6FF', borderBottom: '1px solid #BFDBFE' }}>
              <Box sx={{ flex: 1 }}><Typography fontSize={10} fontWeight={700} color="#1E3A8A" sx={{ letterSpacing: 0.5 }}>EMPLOYEE</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'center' }}><Typography fontSize={10} fontWeight={700} color="#1E3A8A" sx={{ letterSpacing: 0.5 }}>OT HOURS</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'center' }}><Typography fontSize={10} fontWeight={700} color="#1E3A8A" sx={{ letterSpacing: 0.5 }}>OT RATE</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'right' }}><Typography fontSize={10} fontWeight={700} color="#1E3A8A" sx={{ letterSpacing: 0.5 }}>AMOUNT</Typography></Box>
            </Stack>
            {inScope.map((emp, i) => {
              const ot = otOverrides[emp.assignment_id] || { ot_hours: 0, ot_rate: 0 };
              const h = Number(ot.ot_hours) || 0;
              const r = Number(ot.ot_rate) || 0;
              const amt = h * r;
              return (
                <Stack key={emp.assignment_id} direction="row" gap={1} alignItems="center"
                  sx={{ px: 1.5, py: 0.8, borderBottom: i === inScope.length - 1 ? 'none' : '1px solid #DBEAFE' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={12.5} fontWeight={600} color="#111827" noWrap>{emp.fullName}</Typography>
                    <Typography fontSize={10} color="#9CA3AF" sx={{ fontFamily: 'monospace' }}>
                      {emp.assignment_id}{emp.employee_code ? ` · ${emp.employee_code}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'center' }}>
                    <TextField
                      size="small" type="number" value={h || ''}
                      onChange={e => {
                        const v = Math.max(0, Number(e.target.value) || 0);
                        setOtOverrides(prev => ({ ...prev, [emp.assignment_id]: { ...prev[emp.assignment_id], ot_hours: v } }));
                      }}
                      placeholder="0"
                      sx={{ width: 70, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '6px' }, '& input': { textAlign: 'center', py: 0.5 } }}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'center' }}>
                    <TextField
                      size="small" type="number" value={r || ''}
                      onChange={e => {
                        const v = Math.max(0, Number(e.target.value) || 0);
                        setOtOverrides(prev => ({ ...prev, [emp.assignment_id]: { ...prev[emp.assignment_id], ot_rate: v } }));
                      }}
                      placeholder="0"
                      sx={{ width: 70, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '6px' }, '& input': { textAlign: 'center', py: 0.5 } }}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'right' }}>
                    <Typography fontSize={12} fontWeight={700} color="#1E3A8A" sx={{ fontFamily: 'monospace' }}>
                      ₹{fmtINR(amt)}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Action toolbar */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: '12px', border: '1.5px solid #E5E7EB',
        bgcolor: '#FAFBFC' }}>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          {(!isCalc || isReversed) && (
            <Button variant="contained" size="small" disabled={anyBusy}
              startIcon={<PlayArrowIcon sx={{ fontSize: 15 }} />}
              onClick={handleRunPayroll}
              sx={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12.5 }}>
              {isBusy('Run Payroll')
                ? 'Running…'
                : totalLopDays > 0
                  ? `Run Payroll (${totalLopDays} LOP days)`
                  : 'Run Payroll'}
            </Button>
          )}
          {isCalc && !isApp && !isReversed && (
            <Button variant="contained" size="small" disabled={anyBusy}
              startIcon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
              onClick={() => runAction('Approve', () => approvePayrollRun(currentRun.run_id))}
              sx={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12.5 }}>
              {isBusy('Approve') ? 'Approving…' : 'Approve'}
            </Button>
          )}
          {isApp && payslips.length === 0 && (
            <Button variant="contained" size="small" disabled={anyBusy}
              startIcon={<DescriptionIcon sx={{ fontSize: 15 }} />}
              onClick={() => runAction('Generate Payslips', () => generatePayslipsForRun(currentRun.run_id))}
              sx={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12.5 }}>
              {isBusy('Generate Payslips') ? 'Generating…' : 'Generate Payslips'}
            </Button>
          )}
          {isApp && payslips.length > 0 && (
            <Chip icon={<DescriptionIcon sx={{ fontSize: 13 }} />}
              label={`${payslips.length} payslip${payslips.length === 1 ? '' : 's'} generated`}
              size="small"
              sx={{ height: 26, fontSize: 11.5, fontWeight: 600,
                bgcolor: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0' }} />
          )}
          {isApp && payslips.length > 0 && payments.length === 0 && (
            <Button variant="contained" size="small" disabled={anyBusy}
              startIcon={<AccountBalanceIcon sx={{ fontSize: 15 }} />}
              onClick={() => runAction('Create Payments', () => createPaymentsForRun(currentRun.run_id))}
              sx={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12.5 }}>
              {isBusy('Create Payments') ? 'Creating…' : 'Create Payments'}
            </Button>
          )}
          {isApp && payments.length > 0 && (
            <Chip icon={<AccountBalanceIcon sx={{ fontSize: 13 }} />}
              label={`${payments.length} payment${payments.length === 1 ? '' : 's'} created`}
              size="small"
              sx={{ height: 26, fontSize: 11.5, fontWeight: 600,
                bgcolor: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0' }} />
          )}
          {isApp && payments.length > 0 && bankFiles.length === 0 && (
            <Button variant="contained" size="small" disabled={anyBusy}
              startIcon={<AccountBalanceIcon sx={{ fontSize: 15 }} />}
              onClick={() => runAction('Generate Bank File', () => generateBankFileForRun(currentRun.run_id))}
              sx={{ background: 'linear-gradient(135deg,#16a34a,#22c55e)', fontSize: 12.5 }}>
              {isBusy('Generate Bank File') ? 'Generating…' : 'Generate Bank File'}
            </Button>
          )}
          {isApp && bankFiles.length > 0 && (
            <Chip icon={<AccountBalanceIcon sx={{ fontSize: 13 }} />}
              label={`${bankFiles.length} bank file${bankFiles.length === 1 ? '' : 's'}`}
              size="small"
              sx={{ height: 26, fontSize: 11.5, fontWeight: 600,
                bgcolor: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0' }} />
          )}
          {(status === 'completed' || isApp) && (
            <Button variant="outlined" size="small" disabled={anyBusy}
              startIcon={<UndoIcon sx={{ fontSize: 15 }} />}
              onClick={() => runAction('Reverse', () => reversePayrollRun(currentRun.run_id))}
              sx={{ fontSize: 12.5, borderColor: '#FECACA', color: '#DC2626',
                '&:hover': { bgcolor: '#FEF2F2', borderColor: '#EF4444' } }}>
              {isBusy('Reverse') ? 'Reversing…' : 'Reverse'}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Employee table */}
      <Paper sx={{ borderRadius: '12px', border: '1.5px solid #E5E7EB', overflow: 'hidden' }}>
        <Box sx={{ p: 1.5, bgcolor: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Stack direction="row" alignItems="center" gap={0.8}>
            <GroupsIcon sx={{ fontSize: 18, color: '#16a34a' }} />
            <Typography fontSize={13.5} fontWeight={700} color="#111827">
              Employee Results
            </Typography>
            <Chip label={`${filteredEmployees.length} employee${filteredEmployees.length !== 1 ? 's' : ''}`}
              size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 600,
                bgcolor: '#F0FDF4', color: '#16a34a' }} />
          </Stack>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Search employee…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width: 260,
              '& .MuiOutlinedInput-root': { bgcolor: 'white', fontSize: 12.5, borderRadius: '8px' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94A3B8', fontSize: 16 }} />
              </InputAdornment>,
              endAdornment: search ? <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment> : null,
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ p: 2 }}>
            {[...Array(5)].map((_, i) => <Skeleton key={i} height={44} sx={{ mb: 1, borderRadius: 1 }} />)}
          </Box>
        ) : filteredEmployees.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography fontSize={13} color="text.secondary">
              {results.length === 0
                ? 'No results yet — run the payroll to compute employee amounts'
                : 'No matching employees'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Table header */}
            <Stack direction="row" gap={1} sx={{ px: 2, py: 1, bgcolor: '#FAFBFC',
              borderBottom: '1.5px solid #E5E7EB' }}>
              <Box sx={{ flex: 1.6 }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>EMPLOYEE</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'right' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>GROSS</Typography></Box>
              <Box sx={{ width: 100, textAlign: 'right' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>DEDUCTIONS</Typography></Box>
              <Box sx={{ width: 90, textAlign: 'right' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>NET PAY</Typography></Box>
              <Box sx={{ width: 80, textAlign: 'center' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>PAYSLIP</Typography></Box>
              <Box sx={{ width: 80, textAlign: 'center' }}><Typography fontSize={10.5} fontWeight={700} color="#6B7280" sx={{ letterSpacing: 0.5 }}>PAYMENT</Typography></Box>
            </Stack>

            {filteredEmployees.map((e, i) => {
              const psStatus = e.payslip?.status || (e.payslip ? 'generated' : null);
              const pmStatus = e.payment?.payment_status || (e.payment ? 'pending' : null);
              return (
                <Stack key={e.assignment_id} direction="row" gap={1} alignItems="center"
                  sx={{
                    px: 2, py: 1.2,
                    borderBottom: i === filteredEmployees.length - 1 ? 'none' : '1px solid #F3F4F6',
                    '&:hover': { bgcolor: '#F9FAFB' },
                  }}>
                  <Box sx={{ flex: 1.6, minWidth: 0 }}>
                    <Typography fontSize={13} fontWeight={600} color="#111827" noWrap>
                      {e.fullName}
                    </Typography>
                    <Stack direction="row" gap={0.8} alignItems="center">
                      <Typography fontSize={10.5} color="#9CA3AF" sx={{ fontFamily: 'monospace' }}>
                        {e.assignment_id}
                      </Typography>
                      {e.assignment?.employee_code && <>
                        <Typography fontSize={10.5} color="#9CA3AF">·</Typography>
                        <Typography fontSize={10.5} color="#6B7280" sx={{ fontFamily: 'monospace' }}>
                          {e.assignment.employee_code}
                        </Typography>
                      </>}
                    </Stack>
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'right' }}>
                    <Typography fontSize={13} fontWeight={600} color="#111827" sx={{ fontFamily: 'monospace' }}>
                      ₹{fmtINR(e.gross)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 100, textAlign: 'right' }}>
                    <Typography fontSize={13} fontWeight={600} color="#DC2626" sx={{ fontFamily: 'monospace' }}>
                      −₹{fmtINR(e.deductions)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 90, textAlign: 'right' }}>
                    <Typography fontSize={13.5} fontWeight={700} color="#16a34a" sx={{ fontFamily: 'monospace' }}>
                      ₹{fmtINR(e.net)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 80, textAlign: 'center' }}>
                    {psStatus ? (
                      <Chip label={psStatus} size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600,
                          bgcolor: '#DCFCE7', color: '#15803D' }} />
                    ) : (
                      <Typography fontSize={11} color="#D1D5DB">—</Typography>
                    )}
                  </Box>
                  <Box sx={{ width: 80, textAlign: 'center' }}>
                    {pmStatus ? (
                      <Chip label={pmStatus} size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600,
                          bgcolor: pmStatus.toLowerCase().includes('paid') ? '#DCFCE7' : '#FEF3C7',
                          color:   pmStatus.toLowerCase().includes('paid') ? '#15803D' : '#92400E' }} />
                    ) : (
                      <Typography fontSize={11} color="#D1D5DB">—</Typography>
                    )}
                  </Box>
                </Stack>
              );
            })}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// ── Period Card ─────────────────────────────────────────────────────────────
function PeriodCard({ period, payroll, run, onCreate, onResume, onView }) {
  const hasRun     = !!run;
  const status     = hasRun ? (run.run_status || 'draft').toLowerCase() : 'upcoming';
  const isFinal    = ['approved', 'completed'].includes(status);
  const isDraft    = ['draft', 'pending'].includes(status);
  const isReversed = status === 'reversed';

  const statusInfo = hasRun
    ? statusOf(run.run_status)
    : { label: 'UPCOMING', color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE' };

  return (
    <Paper sx={{
      mb: 1.5, p: 2.2, borderRadius: '12px',
      border: '1.5px solid #E5E7EB',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all 0.15s',
      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderColor: '#22c55e' },
    }}>
      <Stack direction="row" alignItems="flex-start" gap={2} sx={{ mb: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          background: hasRun
            ? 'linear-gradient(135deg,#16a34a,#22c55e)'
            : 'linear-gradient(135deg,#3B82F6,#60A5FA)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 3px 10px rgba(34,197,94,0.3)',
          flexShrink: 0,
        }}>
          <CalendarMonthIcon sx={{ fontSize: 22, color: 'white' }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.3 }}>
            <Typography fontSize={16} fontWeight={800} color="#111827" noWrap>
              {period.period_name}
            </Typography>
            <Typography fontSize={11} fontWeight={500} color="#64748B"
              sx={{ fontFamily: 'monospace', bgcolor: '#F3F4F6', px: 0.8, py: 0.1, borderRadius: '5px' }}>
              {period.period_id}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography fontSize={12} color="#6B7280">{payroll?.payroll_name || period.payroll_id}</Typography>
            {period.payment_date && <>
              <Typography fontSize={12} color="#6B7280">·</Typography>
              <Typography fontSize={12} color="#6B7280">Pay Date {period.payment_date}</Typography>
            </>}
            {hasRun && <>
              <Typography fontSize={12} color="#6B7280">·</Typography>
              <Typography fontSize={11} color="#6B7280" sx={{ fontFamily: 'monospace' }}>
                {run.run_id}
              </Typography>
            </>}
          </Stack>
        </Box>

        <Chip
          label={statusInfo.label.toUpperCase()}
          size="small"
          sx={{
            height: 26, fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
            px: 1.6, borderRadius: '8px',
            bgcolor: statusInfo.bg, color: statusInfo.color,
            border: `1px solid ${statusInfo.border}`,
          }}
        />
      </Stack>

      {hasRun && (
        <>
          <Divider sx={{ mb: 1.5 }} />
          <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Box sx={{ minWidth: 100 }}>
              <Typography fontSize={10.5} color="#9CA3AF" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Employees</Typography>
              <Typography fontSize={15} fontWeight={700} sx={{ fontFamily: 'monospace' }}>{run.total_employees || 0}</Typography>
            </Box>
            <Box sx={{ minWidth: 110 }}>
              <Typography fontSize={10.5} color="#9CA3AF" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Gross</Typography>
              <Typography fontSize={15} fontWeight={700} sx={{ fontFamily: 'monospace' }}>₹{fmtCompact(run.total_gross || 0)}</Typography>
            </Box>
            <Box sx={{ minWidth: 110 }}>
              <Typography fontSize={10.5} color="#9CA3AF" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Deductions</Typography>
              <Typography fontSize={15} fontWeight={700} color="#DC2626" sx={{ fontFamily: 'monospace' }}>₹{fmtCompact(run.total_deductions || 0)}</Typography>
            </Box>
            <Box sx={{ minWidth: 110 }}>
              <Typography fontSize={10.5} color="#9CA3AF" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Net Pay</Typography>
              <Typography fontSize={15} fontWeight={700} color="#16a34a" sx={{ fontFamily: 'monospace' }}>₹{fmtCompact(run.total_net || 0)}</Typography>
            </Box>
          </Stack>
        </>
      )}

      <Divider sx={{ mb: 1.5 }} />

      <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
        {!hasRun && (
          <Button variant="contained" size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={() => onCreate(period)}
            sx={{
              background: 'linear-gradient(135deg,#16a34a,#22c55e)',
              fontSize: 12.5, fontWeight: 700, py: 0.7, px: 2,
              '&:hover': { background: 'linear-gradient(135deg,#15803d,#16a34a)' },
            }}>
            Create Pay Run
          </Button>
        )}
        {hasRun && isDraft && (
          <Button variant="contained" size="small"
            startIcon={<PlayArrowIcon sx={{ fontSize: 15 }} />}
            onClick={() => onResume(period, run)}
            sx={{
              background: 'linear-gradient(135deg,#16a34a,#22c55e)',
              fontSize: 12.5, fontWeight: 700, py: 0.7, px: 2,
              '&:hover': { background: 'linear-gradient(135deg,#15803d,#16a34a)' },
            }}>
            Resume Pay Run
          </Button>
        )}
        {hasRun && isReversed && (
          <Button variant="contained" size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={() => onCreate(period)}
            sx={{
              background: 'linear-gradient(135deg,#16a34a,#22c55e)',
              fontSize: 12.5, fontWeight: 700, py: 0.7, px: 2,
            }}>
            Re-create Pay Run
          </Button>
        )}
        {hasRun && (
          <Button variant="outlined" size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
            onClick={() => onView(run)}
            sx={{
              fontSize: 12.5, py: 0.6,
              borderColor: '#E5E7EB', color: '#374151',
              '&:hover': { borderColor: '#22c55e', color: '#16a34a', bgcolor: '#f0fdf4' },
            }}>
            View Details
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function PayrollRunPage({ runs, onRefresh }) {
  // view: 'periods' | 'workspace' | 'detail'
  const [view, setView]                       = useState('periods');
  const [workspacePeriodId, setWorkspacePeriodId] = useState(null);
  const [workspaceRunId, setWorkspaceRunId]   = useState(null);
  const [selectedRunId, setSelectedRunId]     = useState(null);
  const [periods, setPeriods]                 = useState([]);
  const [payrolls, setPayrolls]               = useState([]);
  const [loadingMeta, setLoadingMeta]         = useState(true);
  const [search, setSearch]                   = useState('');
  const [pfAlerts, setPfAlerts]               = useState([]);

  // Load periods + payrolls once
  useEffect(() => {
    setLoadingMeta(true);
    Promise.all([
      fetchRecords('PAY_PERIOD').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL').catch(() => ({ data: { data: [] } })),
      fetchRecords('PF_ACCOUNT').catch(() => ({ data: { data: [] } }))
    ]).then(([pp, pr, pfRes]) => {
      setPeriods(pp.data.data || []);
      setPayrolls(pr.data.data || []);
      const today = new Date().toISOString().split('T')[0];
      const upcomingPFs = (pfRes.data.data || []).filter(pf => pf.pending_update_date && pf.pending_update_date > today);
      setPfAlerts(upcomingPFs);
      setLoadingMeta(false);
    });
  }, []);

  // Workspace view
  if (view === 'workspace') {
    return (
      <ProcessPayRunPage
        periodId={workspacePeriodId}
        existingRunId={workspaceRunId}
        onBack={() => { setView('periods'); setWorkspacePeriodId(null); setWorkspaceRunId(null); }}
        onDone={() => {
          setView('periods');
          setWorkspacePeriodId(null);
          setWorkspaceRunId(null);
          onRefresh?.();
        }}
      />
    );
  }

  // Detail view (existing component, for already-processed runs)
  if (view === 'detail') {
    const selectedRun = runs.find(r => r.run_id === selectedRunId);
    if (!selectedRun) {
      setView('periods');
      return null;
    }
    return <RunDetail run={selectedRun}
      onBack={() => { setView('periods'); setSelectedRunId(null); onRefresh?.(); }}
      onRefresh={onRefresh} />;
  }

  // ── Default view: Period-centric list ────────────────────────────────────
  // Map each period to its latest non-reversed run (if any)
  const periodToRun = {};
  for (const r of runs) {
    if (!r.period_id) continue;
    const existing = periodToRun[r.period_id];
    // Prefer non-reversed; among non-reversed, prefer latest by created_at/id
    if (!existing) { periodToRun[r.period_id] = r; continue; }
    const exReversed = (existing.run_status || '').toLowerCase() === 'reversed';
    const rReversed  = (r.run_status || '').toLowerCase() === 'reversed';
    if (exReversed && !rReversed) periodToRun[r.period_id] = r;
    else if (!exReversed && !rReversed && r.id > existing.id) periodToRun[r.period_id] = r;
  }

  const sortedPeriods = [...periods].sort((a, b) => {
    // newer periods first
    const ad = a.start_date || '';
    const bd = b.start_date || '';
    return bd.localeCompare(ad);
  });

  const filteredPeriods = sortedPeriods.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const r = periodToRun[p.period_id];
    return (p.period_name || '').toLowerCase().includes(q)
      || (p.period_id || '').toLowerCase().includes(q)
      || (p.payroll_id || '').toLowerCase().includes(q)
      || (r?.run_name || '').toLowerCase().includes(q);
  });

  const handleCreate = (period) => {
    setWorkspacePeriodId(period.period_id);
    setWorkspaceRunId(null);
    setView('workspace');
  };
  const handleResume = (period, run) => {
    setWorkspacePeriodId(period.period_id);
    setWorkspaceRunId(run.run_id);
    setView('workspace');
  };
  const handleView = (run) => {
    setSelectedRunId(run.run_id);
    setView('detail');
  };

  return (
    <Box>
      {pfAlerts.length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: '8px', bgcolor: '#FEF9C3', border: '1px solid #FEF08A', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ mt: 0.2 }}><VisibilityIcon sx={{ fontSize: 18, color: '#A16207' }} /></Box>
          <Box>
            {pfAlerts.map(pf => {
              const d = pf.pending_update_date.split('-').reverse().join('-');
              return (
                <Typography key={pf.pf_id} fontSize={13} fontWeight={600} color="#854D0E" sx={{ mb: 0.5 }}>
                  Assignment ID: {pf.assignment_id} — PF % and contribution values will be updated from the Effective Date: {d}.
                </Typography>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Toolbar */}
      <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search period or payroll…"
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 },
            '& .MuiOutlinedInput-root': { bgcolor: 'white', fontSize: 13, borderRadius: '10px' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start">
              <SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} />
            </InputAdornment>,
            endAdornment: search ? <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearch('')}>
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </InputAdornment> : null,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" alignItems="center" gap={0.6}
          sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981' }} />
          <Typography fontSize={12} fontWeight={600} color="text.secondary">
            {filteredPeriods.length} period{filteredPeriods.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>
      </Stack>

      {/* List */}
      {loadingMeta ? (
        [...Array(3)].map((_, i) => <Skeleton key={i} height={140} sx={{ mb: 1.5, borderRadius: '12px' }} />)
      ) : filteredPeriods.length === 0 ? (
        <Paper sx={{ py: 8, textAlign: 'center', borderRadius: '12px', border: '1.5px solid #E5E7EB' }}>
          <Typography fontSize={14} fontWeight={600} color="#374151">
            {search ? 'No matching periods' : 'No pay periods yet'}
          </Typography>
          <Typography fontSize={12.5} color="#9CA3AF" sx={{ mt: 0.5 }}>
            {search
              ? 'Try clearing search'
              : 'Pay periods are auto-generated for active payrolls. Wait for the daily scheduler or trigger via Pay Foundation → Payroll.'}
          </Typography>
        </Paper>
      ) : (
        filteredPeriods.map(p => (
          <PeriodCard key={p.period_id}
            period={p}
            payroll={payrolls.find(pr => pr.payroll_id === p.payroll_id)}
            run={periodToRun[p.period_id]}
            onCreate={handleCreate}
            onResume={handleResume}
            onView={handleView}
          />
        ))
      )}
    </Box>
  );
}
